/**
 * Sentry SDK for React Frontend
 * 
 * This module initializes Sentry for error tracking, performance monitoring,
 * and user session replay in the React application.
 * 
 * Features:
 * - Automatic error boundary for React component crashes
 * - Browser tracing for performance monitoring
 * - Session replay for debugging user sessions
 * - User context tracking
 * - Breadcrumbs for user actions
 * - Custom error capture with context
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for the React application
 * Call this BEFORE rendering the React app
 */
export function initSentry() {
  // Use the same DSN as Edge Functions
  const dsn = 'https://00add916266f8891a355e97355c667d7@o4510492986179584.ingest.us.sentry.io/4510492994895872';
  
  Sentry.init({
    dsn,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        // Set tracing origins to connect frontend traces to backend
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/swrgewlinwxnljlxaiwc\.supabase\.co/,
          /^https:\/\/.*\.supabase\.co/,
        ],
      }),
      Sentry.replayIntegration({
        // Mask all text content by default for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance Monitoring sample rate
    tracesSampleRate: 1.0, // 100% of transactions for monitoring
    
    // Session Replay sample rate
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Environment
    environment: import.meta.env.MODE || 'production',
    
    // Release version (from package.json)
    release: import.meta.env.VITE_APP_VERSION,
    
    // Don't send errors in development
    enabled: import.meta.env.MODE !== 'development',
    
    // Before sending errors, filter out known issues
    beforeSend(event, hint) {
      // Filter out network errors from ad blockers
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message);
        if (message.includes('Extension context invalidated')) {
          return null; // Don't send Chrome extension errors
        }
        if (message.includes('ResizeObserver loop')) {
          return null; // Don't send ResizeObserver errors
        }
      }
      
      return event;
    },
  });
}

/**
 * Set user context for all future events
 * Call this after user signs in
 */
export function setUser(userId: string, email?: string, username?: string) {
  Sentry.setUser({
    id: userId,
    email: email,
    username: username,
  });
}

/**
 * Clear user context
 * Call this after user signs out
 */
export function clearUser() {
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb to track user actions
 * This helps understand what led to an error
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user-action',
  data?: Record<string, any>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}

/**
 * Capture an exception with custom context
 * Use this for try-catch blocks where you want to report errors
 */
export function captureException(
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: Sentry.SeverityLevel;
  }
) {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    
    if (context?.level) {
      scope.setLevel(context.level);
    }
    
    Sentry.captureException(error);
  });
}

/**
 * Capture a message (non-error event)
 * Use this for warnings or informational messages
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, any>
) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    
    Sentry.captureMessage(message, level);
  });
}

/**
 * Set custom context for debugging
 * Example: setContext('payment', { amount: 100, currency: 'USD' })
 */
export function setContext(name: string, context: Record<string, any>) {
  Sentry.setContext(name, context);
}

/**
 * Start a new transaction for performance monitoring
 * Use this for tracking long operations (API calls, page loads, etc.)
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startTransaction({
    name,
    op,
  });
}

// Export ErrorBoundary for wrapping React components
export { ErrorBoundary } from '@sentry/react';

// Export Sentry for advanced usage
export { Sentry };
